import React from 'react'
import {Link, useLocation, useParams} from 'react-router-dom';
import {ErrorBoundary} from 'react-error-boundary';
import {useCartState} from '../../../contexts/cart';
import {graphql, useLazyLoadQuery, useFragment} from 'react-relay/hooks';
import {Helmet} from 'react-helmet-async';
import Marked from '../../../components/Marked';
import i18n from '../../../i18n';
import useQuery from '../../../useQuery';
import {FormattedMessage} from 'react-intl';
import useNow from '../../../useNow';
import {adopt} from '../../../math';
import {IN, OUT_NEGATIVE, DEFAULT_SERVICE_DESCRIPTION_SEPARATOR} from '../../../constants';
import ErrorFallback from '../../../components/ErrorFallback';
import Browser from '../../../components/Browser';
import ButtonIcon from '../../../components/button-icon/button-icon.component';
import CartMinusCircle from '../../../assets/icons/cart-minus-circle.svg';
import CartAddCircle from '../../../assets/icons/cart-add-circle.svg';
import EndpointsHeader from './endpoints-header';
import ServiceSchedule, {SERVICE_DESCRIPTION, ServiceScheduleCard} from './service-schedule';
import {isTrue} from '../../../utility';
import EndpointSchedule from './endpoint-schedule';
import './basic.scss';
import classNames from 'classnames';
import getTagValue from '../../../utilities/get-tag-value';
import SuspenseImageDynamicSize from '../../../components/SuspenseImageDynamicSize';
import CalendarIcon from '../../../assets/icons0/calendar.svg';
import {format} from 'date-fns';
import {getServiceEndDate, getServiceStartDate} from './get-service-date';
import FacebookIcon from '../../../assets/icons0/socials/facebook-icon.svg';
import InstagramIcon from '../../../assets/icons0/socials/instagram-icon.svg';
import TelegramIcon from '../../../assets/icons0/socials/telegram-icon.svg';
import CheckServiceState from './check-service-state';
import getServiceUrl from '../../../utilities/get-service-url';
import UpcomingEvents from './upcoming-events';
import getAvailableEndpoints from '../../../utilities/get-available-endpoints';
import getLowestEndpointPrice from '../../../utilities/get-lowest-endpoint-price';
import InfoCircle01Icon from '../../../assets/icons0/info-circle-01.svg';
import Currency from '../../../components/currency/currency';
import useLocalCart from '../local-cart/use-local-cart';
import Cart from '../local-cart/cart';
import Apply from '../../../components/Apply';
import {ErrorMessagesProvider} from '../../checkout/error-messages-context';
import SeoMetaTags, {SeoAlternateHreflang, SeoCanonical} from '../../../components/seo-meta-tags';
import {ServiceSchemaScript} from '../../../components/schema-script';

const Endpoint = React.memo(({endpoint, serviceByIdOrSlug, incrementEndpoint, decrementEndpoint, addEndpoint, theme, buyAvailable, isStem = false, showWarning = false}) => {
    const {locale = 'uk', more} = useQuery();
    const state = useCartState();
    const [isAnimationRunning, setIsAnimationRunning] = React.useState(false);
    const fEndpoint = useFragment(
        graphql`
            fragment serviceBasicEndpoint_endpoint on Endpoint {
                id
                name
                nameUk
                quantity
                price
                start
                end
                strategy
                every
                description
                descriptionUk
                untie
                hints
                tags
                labels
            }
        `,
        endpoint
    );
    const fServiceByIdOrSlug = useFragment(
        graphql`
            fragment serviceBasicEndpoint_serviceByIdOrSlug on Service {
                id
                nameUk
                name
                target
            }
        `,
        serviceByIdOrSlug
    );
    const cartEntry = React.useMemo(() =>
        state.find(e => e.serviceId === fServiceByIdOrSlug.id && e.endpointId === fEndpoint.id),
    [state, fEndpoint.id, fServiceByIdOrSlug.id]);
    const now = useNow();
    const dpt = React.useMemo(() => adopt(now, fEndpoint.start, fEndpoint.end, fEndpoint.strategy,
        fEndpoint.every), [now, fEndpoint]);
    const addEndpointToCart = () => {
        if(!cartEntry) {
            setIsAnimationRunning(true);
            addEndpoint({
                endpointId: fEndpoint.id,
                serviceId: fServiceByIdOrSlug.id,
                ...(fEndpoint.untie && {untie: {price: data.input, currency: 'uah'}}) 
            });
        }
    };
    const alternativePriceTag = fEndpoint.tags && fEndpoint.tags.length && fEndpoint.tags.find(tag => tag.includes('price'));
    const targetDate = getTagValue(fEndpoint.tags, 'targetDate');
    const serviceTargetDate = fServiceByIdOrSlug.target ? format(new Date(fServiceByIdOrSlug.target), 'dd.MM.yyyy') : null;
    return (
        <div className={classNames('basic-endpoint border-radius-0dot75rem border-1px-solid padding-1dot25rem mw768-padding-2rem', {'basic-endpoint--stem': isStem})}>
            <div className='display-flex flex-direction-column-row'>
                <div className=' mw768-flex-1 mw768-margin-right-1rem'>
                    <div className='basic-endpoint__title text-lg medium mw768-text-xl'>
                        {i18n(fServiceByIdOrSlug, 'name', locale)} | {i18n(fEndpoint, 'name', locale)}
                    </div>
                    {targetDate ? 
                        <div className='basic-endpoint__date text-sm mw768-text-md padding-top-0dot5rem'>{targetDate}</div>
                        :
                        serviceTargetDate && <div className='basic-endpoint__date text-sm mw768-text-md padding-top-0dot5rem'>{serviceTargetDate}</div>
                    }
                    {((dpt.state === IN && fEndpoint.quantity < 16) || isTrue(more)) && <div className='padding-top-0dot5rem color-warning-500 text-sm'><FormattedMessage defaultMessage='Left'/> {fEndpoint.quantity}</div>}
                    <EndpointSchedule {...{
                        start: fEndpoint.start,
                        end: fEndpoint.end,
                        strategy: fEndpoint.strategy,
                        every: fEndpoint.every,
                        theme
                    }}/>
                </div>
                {dpt.state === IN &&
                    <div className='display-flex mw768-flex-1 justify-content-space-between align-items-center mw768-align-self-center mw768-justify-content-flex-end padding-top-1rem mw768-padding-top-0'>
                        <p className='basic-endpoint__price mw768-margin-right-2dot5rem display-flex'>
                            <span className='padding-right-0dot125rem'>{fEndpoint.price} </span>
                            <Currency {...{defaultCurrency: <FormattedMessage defaultMessage='UAH'/>, isStem, theme, stemIconClasses: 'display-block width-1dot25rem height-1dot25rem'}}/>
                            {alternativePriceTag && 
                                <span className='padding-left-0dot125rem'>`(${alternativePriceTag.replace('price=', '')})`</span>
                            }
                        </p>
                        <div className='width-7rem min-height-2dot625rem position-relative basic-endpoint__btn-container'>
                            {fEndpoint.quantity > 0 &&
                                <Browser>
                                    <div className={classNames('basic-endpoint__btn border-1px-solid padding-0dot5rem border-radius-0dot5rem text-md medium display-flex position-relative justify-content-center shadow-xs', {'basic-endpoint__btn--empty cursor-pointer width-100percent buy-btn-animation--backwards': !cartEntry, 'basic-endpoint__btn--entry buy-btn-animation--forward': !!cartEntry, 'buy-btn-animation--play': isAnimationRunning, 'basic-endpoint__btn--disabled': !buyAvailable || showWarning})} onClick={() => addEndpointToCart()}>
                                        <span className={classNames({'add-to-cart--visible': !cartEntry, 'add-to-cart--hidden': !!cartEntry})}><FormattedMessage defaultMessage='Buy'/></span>
                                        <div className={classNames({'decrement-endpoint--visible': !!cartEntry, 'decrement-endpoint--hidden': !cartEntry})}>
                                            <ButtonIcon {...{clickHandler: () => decrementEndpoint(cartEntry)}}>
                                                <CartMinusCircle className={classNames('width-100percent')}/>
                                            </ButtonIcon>
                                        </div>
                                        <p className={classNames('text-md text-align-center', {'endpoint-quantity--visible': !!cartEntry, 'endpoint-quantity--hidden': !cartEntry, 'color-gray-100': theme === 'dark', 'color-gray-700': theme === 'light'})}>{cartEntry ? cartEntry.quantity : ''}</p>
                                        <div className={classNames({'increment-endpoint--visible': !!cartEntry, 'increment-endpoint--hidden': !cartEntry})}>
                                            <ButtonIcon {...{clickHandler: () => incrementEndpoint(cartEntry)}}>
                                                <CartAddCircle className={classNames('width-100percent')}/>
                                            </ButtonIcon>
                                        </div>
                                    </div>
                                </Browser>
                            }
                            {showWarning &&
                                <div className='basic-endpoint__btn-tooltip'>
                                    {!isStem && 
                                        <p className='text-xs medium display-flex'>
                                            <Currency {...{isStem: true, theme, stemIconClasses: 'display-block width-1rem height-1rem'}}/>
                                            <span className='padding-left-0dot125rem'><FormattedMessage defaultMessage="ticket already in your cart"/></span>
                                        </p>
                                    }
                                    {isStem && <p className='text-xs medium'><FormattedMessage defaultMessage="UAH ticket is already in your cart"/></p>}
                                </div>
                            }
                        </div>
                    </div>
                }
            </div>
        </div>
    );
});

const useStemCart = ({cartState, service}) => {
    const [stemCart, setStemCart] = React.useState(false);
    React.useEffect(() => {
        if (cartState.length) {
            const endpointIdsInCart = cartState.map(e => e.endpointId);
            const endpointsInCart = service.endpoints.filter(e => endpointIdsInCart.includes(e.id));
            setStemCart(endpointsInCart.some(e => e.labels && e.labels.isStem));
        }
    }, [cartState, service]);
    return {
        stemCart
    }
};

const useUpcomingEvents = ({service}) => {
    const upcomingEventsList = React.useMemo(() => {
        return service.upcoming.filter((e => e.id !== service.id));
    }, [service]);
    return {
        upcomingEventsList
    }
};

const Service = ({ticketsBoxRef}) => {
    const {idOrSlug} = useParams();
    const {more, locale = 'uk'} = useQuery();
    const location = useLocation();
    const {serviceByIdOrSlug} = useLazyLoadQuery(
        graphql`
            query serviceBasicQuery($payload: String) {
                serviceByIdOrSlug(payload: $payload) {
                    id
                    name
                    nameUk
                    details
                    detailsUk
                    description
                    location
                    start
                    end
                    strategy
                    every
                    target
                    slug
                    warp
                    merchantAccount
                    uaPayMerchantId
                    tzzApiKey
                    lqpPublic
                    merchant {
                        id
                    }
                    paymentMethod {
                        id
                    }
                    endpoints {
                        id
                        quantity
                        start
                        end
                        strategy
                        every
                        price
                        tags
                        labels
                        ...serviceBasicEndpoint_endpoint
                    }
                    upcoming {
                        id
                    }
                    ...serviceBasicEndpoint_serviceByIdOrSlug
                }
            }
        `,
        {payload: decodeURIComponent(idOrSlug)}
    );
    const {
        localCartState,
        localCartStateDeferred,
        cartVisibility,
        setCartVisibility,
        decrementEndpoint,
        incrementEndpoint,
        addEndpoint,
        checkout,
        deleteEndpoint,
        isLocalCartDeferred
    } = useLocalCart(serviceByIdOrSlug.id);
    
    const tags = serviceByIdOrSlug.description ? serviceByIdOrSlug.description.split(DEFAULT_SERVICE_DESCRIPTION_SEPARATOR) : [];
    const theme = getTagValue(tags, 'theme', {defaultValue: 'dark'});
    const posterUrl = getTagValue(tags, 'posterUrl');
    const targetEndVal = getTagValue(tags, 'targetEnd');
    const seoDescription = decodeURI(getTagValue(tags, 'seoDescription', {defaultValue: ''}));
    const seoTitle = decodeURI(getTagValue(tags, 'seoTitle', {defaultValue: ''}));
    const target = serviceByIdOrSlug.target ? format(new Date(serviceByIdOrSlug.target), 'dd-MM-yyyy') : null;
    const targetEnd = targetEndVal ? format(new Date(targetEndVal), 'dd-MM-yyyy') : null;
    const {stemCart} = useStemCart({cartState: localCartState, service: serviceByIdOrSlug});
    const lowestTicketPrice = React.useMemo(() => {
        return getLowestEndpointPrice(serviceByIdOrSlug.endpoints);
    }, [serviceByIdOrSlug]);
    const locationName = serviceByIdOrSlug.location;
    const facebook = getTagValue(tags, 'facebook');
    const instagram = getTagValue(tags, 'instagram');
    const telegram = getTagValue(tags, 'telegram');
    const showTicketsPrice = isTrue(getTagValue(tags, 'showTicketsPrice', {defaultValue: 'false'}));
    const showEventLocation = isTrue(getTagValue(tags, 'showEventLocation', {defaultValue: 'false'}));
    const now = useNow();
    const serviceStart = getServiceStartDate(serviceByIdOrSlug.endpoints);
    const serviceEnd = getServiceEndDate(serviceByIdOrSlug.endpoints, serviceByIdOrSlug);
    const {state} = React.useMemo(() => adopt(now, serviceStart, serviceEnd, serviceByIdOrSlug.strategy,
        serviceByIdOrSlug.every), [now, serviceByIdOrSlug]);
    const [buyAvailable, setBuyAvailable] = React.useState(true);
    const {upcomingEventsList} = useUpcomingEvents({service: serviceByIdOrSlug});
    
    const availableEndpoints = React.useMemo(() => {
        return getAvailableEndpoints({endpoints: serviceByIdOrSlug.endpoints, now, showAll: isTrue(more)});
    }, [now, serviceByIdOrSlug]);
    const availableCurrencyEndpoints = React.useMemo(() => {
        return availableEndpoints.filter(e => !e.labels || !e.labels.isStem);
    }, [availableEndpoints]);
    const availableStemEndpoints = React.useMemo(() => {
        return availableEndpoints.filter(e => e.labels && e.labels.isStem);
    }, [availableEndpoints]);
    const hasEndpointsToSell = React.useMemo(() => {
        return availableEndpoints.length > 0;
    }, [availableEndpoints]);
    const defaultDescription = `Придбати квиток на ⭐️ ${i18n(serviceByIdOrSlug, 'name', locale)}${target ? ` ☝${target}` : ''}${lowestTicketPrice ? `. Від ${lowestTicketPrice} грн` : ''} | Купити квитки онлайн`;
    const serviceUrl = getServiceUrl({description: serviceByIdOrSlug.description, id: serviceByIdOrSlug.id, slug: serviceByIdOrSlug.slug, warp: serviceByIdOrSlug.warp, location: {search: ''}});
    const fbPId = getTagValue(tags, 'fbPId');
    return (
        <>
        <SeoMetaTags {...{
            meta: {
                title: seoTitle || `${i18n(serviceByIdOrSlug, 'name', locale)}: Купити квитки на ottry.com`,
                description: seoDescription || defaultDescription
            }
        }}/>
        <ServiceSchemaScript {...{service: serviceByIdOrSlug}}/>
        <Helmet>
            {!!posterUrl && <meta property='og:image' content={`https://${posterUrl}`}/>}
            <meta property="og:type" content="article"/>
        </Helmet>
        <SeoCanonical {...{urlPath: serviceUrl}}/>
        <SeoAlternateHreflang {...{urlPath: serviceUrl, locationSearch: location.search, en: serviceByIdOrSlug.name && serviceByIdOrSlug.nameUk}}/>
        <div data-page-type='event' data-insights-object-id={serviceByIdOrSlug.id} data-service-slug={serviceByIdOrSlug.slug} data-fbp-id={fbPId}>
            <div className={`view theme theme--${theme}`}>
                {posterUrl &&
                    <div className='basic__poster-img-container max-height-35rem mw768-max-height-37dot5rem margin-0-auto max-width-100vw mw768-max-width-76rem display-flex justify-content-center'>
                        <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                            <SuspenseImageDynamicSize
                                className='basic__poster-img'
                                src={`https://${posterUrl}`}
                            />
                        </ErrorBoundary>
                    </div>
                }
                <div className='block'>
                    <div className='max-width-50rem margin-0-auto'>
                        <h1 className='basic__title display-sm semibold padding-top-2rem mw768-padding-top-4rem'>
                            {i18n(serviceByIdOrSlug, 'name', locale)}
                        </h1>
                        {target &&
                            <div className='padding-top-0dot5rem basic__target display-flex'>
                                <CalendarIcon className='display-block height-1dot25rem width-1dot25rem'/>
                                <p className='text-md medium padding-left-0dot5rem'>{target}</p>
                                {targetEnd && target !== targetEnd &&
                                    <span className='text-md medium padding-left-0dot25rem'>{` — ${targetEnd}`}</span>
                                }
                            </div>
                        }
                        <div className='padding-top-1dot5rem'>
                            <Marked {...{content: i18n(serviceByIdOrSlug, 'details', locale)}}/>
                        </div>
                        {serviceByIdOrSlug.endpoints && serviceByIdOrSlug.endpoints.length && showTicketsPrice && 
                            <p className='marked text-md'>
                                <span className='bold'><FormattedMessage defaultMessage='Price: from'/> </span>
                                <span>{lowestTicketPrice} UAH</span>
                            </p>
                        }
                        {showEventLocation && 
                            <p className='marked text-md'>
                                <span className='bold'><FormattedMessage defaultMessage='Location'/>: </span>
                                <span>{ locationName ? decodeURI(locationName) : <FormattedMessage defaultMessage='To be announced'/>}</span>
                            </p>
                        }
                        {(facebook || instagram || telegram) &&
                            <div className='padding-top-1dot25rem mw768-padding-top-3rem display-flex gap-1dot25rem'>
                                {facebook &&
                                    <a href={facebook} target='_blank' rel='noopener noreferrer'>
                                        <FacebookIcon className='basic__social-icon display-block height-1dot5rem mw768-height-2rem width-1dot5rem mw768-width-2rem'/>
                                    </a>
                                }
                                {instagram &&
                                    <a href={instagram} target='_blank' rel='noopener noreferrer'>
                                        <InstagramIcon className='basic__social-icon display-block height-1dot5rem mw768-height-2rem width-1dot5rem mw768-width-2rem'/>
                                    </a>
                                }
                                {telegram &&
                                    <a href={telegram} target='_blank' rel='noopener noreferrer'>
                                        <TelegramIcon className='basic__social-icon display-block height-1dot5rem mw768-height-2rem width-1dot5rem mw768-width-2rem'/>
                                    </a>
                                }
                            </div>
                        }
                        <div className='margin-top-1rem mw768-margin-top-3rem padding-top-2rem padding-bottom-2rem basic__endpoints-container' ref={ticketsBoxRef}>
                            <EndpointsHeader {...{serviceByIdOrSlug: serviceByIdOrSlug, localCartState: localCartStateDeferred, isLocalCartDeferred, setCartVisibility, showCart: state !== OUT_NEGATIVE, isStem: stemCart}}/>
                            <div className='padding-top-2rem'>
                                <div>
                                    {i18n(serviceByIdOrSlug, 'details', locale) && i18n(serviceByIdOrSlug, 'details', locale).endsWith('!') ?
                                        <div className='padding-bottom-2rem'>
                                            <ServiceScheduleCard {...{...SERVICE_DESCRIPTION[OUT_NEGATIVE].default, theme}}/>
                                        </div>
                                        :
                                        <>
                                            <ServiceSchedule {...{
                                                start: serviceStart,
                                                end: serviceEnd,
                                                strategy: serviceByIdOrSlug.strategy,
                                                every: serviceByIdOrSlug.every,
                                                state,
                                                theme
                                            }}/>
                                            {state === IN &&
                                                <div className='display-flex flex-direction-column row-gap-1dot5rem'>
                                                    {availableCurrencyEndpoints.map((endpoint) =>
                                                        <Endpoint key={endpoint.id} {...{endpoint, serviceByIdOrSlug: serviceByIdOrSlug, incrementEndpoint, decrementEndpoint, addEndpoint, theme, buyAvailable, showWarning: !!localCartState.length && stemCart}}/>
                                                    )}
                                                    <Apply {...{on: 'stem'}}>
                                                        {!!availableStemEndpoints.length && 
                                                            <div>
                                                                <p className='basic__partner-title display-flex align-items-center padding-bottom-1rem position-relative z-index-1'>
                                                                    <span className='text-md medium'><FormattedMessage defaultMessage='Partner tickets'/></span>
                                                                    <Link className='padding-left-0dot5rem'>
                                                                        <InfoCircle01Icon className='display-block width-1rem height-1rem'/>
                                                                    </Link>
                                                                </p>
                                                                <div className='text-md medium'>
                                                                    {availableStemEndpoints.map((endpoint) =>
                                                                        <Endpoint key={endpoint.id} {...{endpoint, serviceByIdOrSlug: serviceByIdOrSlug, incrementEndpoint, decrementEndpoint, addEndpoint, theme, buyAvailable, isStem: true, showWarning: !!localCartState.length && !stemCart}}/>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        }
                                                    </Apply>
                                                    {!hasEndpointsToSell && <ServiceScheduleCard {...{...SERVICE_DESCRIPTION[OUT_NEGATIVE].default, theme}}/>}
                                                </div>
                                            }
                                        </>
                                    }
                                    {(state === OUT_NEGATIVE || !hasEndpointsToSell) && upcomingEventsList.length > 0 &&
                                        <div className='padding-top-2rem'>
                                            <UpcomingEvents {...{serviceList: upcomingEventsList}}/>
                                        </div>
                                    }
                                </div>
                            </div>
                            {state === IN && hasEndpointsToSell && <p className='text-sm mw768-text-md color-gray-400 padding-top-2rem position-relative z-index-1'><FormattedMessage defaultMessage='* After payment, a letter with a QR code will be sent to your e-mail address, which must be presented at the entrance. Please note that in the case of using the Gmail mail service, the letter with the ticket may end up in the "Alerts", "Promotions" or "Social networks" folder.'/></p>}
                        </div>
                    </div>
                </div>
            </div>
            <CheckServiceState {...{serviceByIdOrSlug: serviceByIdOrSlug, setBuyAvailable}}/>
            <ErrorMessagesProvider>
                <Cart {...{cartVisible: cartVisibility, decrementEndpoint, incrementEndpoint, serviceById: serviceByIdOrSlug, localCartState: localCartStateDeferred, isLocalCartDeferred, setCartVisibility, checkout, stemCart, deleteEndpoint, availableEndpoints}}/>
            </ErrorMessagesProvider>
        </div>
        </>
    )
};

export default React.memo(Service);